// Search-state for the locations index route. The route in AppRouter.tsx
// owns the validateSearch schema; this hook is a typed surface over
// TanStack's useSearch / useNavigate so consumers don't touch the router
// primitives directly. Sort / query changes reset page so previous
// pagination state never bleeds into the new view.
//
// Mirrors usePresetMenusRouterSearch minus status/ownerIds — locations
// have no status or owner concept.

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';

export type LocationSortColumn = 'postal_code' | '';
export type LocationSortDirection = 'asc' | 'desc' | '';

export interface LocationsSearchParams {
  q: string;
  page: number;
  perPage: number;
  sort: LocationSortColumn;
  direction: LocationSortDirection;
}

export const LOCATIONS_SEARCH_DEFAULTS: LocationsSearchParams = {
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: '',
  direction: '',
};

const LOCATIONS_ROUTE = '/locations' as const;

const VALID_SORTS: ReadonlySet<LocationSortColumn> = new Set(['postal_code', '']);
const VALID_DIRECTIONS: ReadonlySet<'asc' | 'desc'> = new Set(['asc', 'desc']);

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parseLocationsSearch(raw: Record<string, unknown>): LocationsSearchParams {
  const sort: LocationSortColumn = VALID_SORTS.has(raw.sort as LocationSortColumn)
    ? (raw.sort as LocationSortColumn)
    : LOCATIONS_SEARCH_DEFAULTS.sort;
  const direction: LocationSortDirection = VALID_DIRECTIONS.has(raw.direction as 'asc' | 'desc')
    ? (raw.direction as 'asc' | 'desc')
    : LOCATIONS_SEARCH_DEFAULTS.direction;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  return {
    q: typeof raw.q === 'string' ? raw.q : LOCATIONS_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : LOCATIONS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0 ? perPage : LOCATIONS_SEARCH_DEFAULTS.perPage,
    sort,
    direction,
  };
}

export function useLocationsRouterSearch() {
  const search = useSearch({ from: LOCATIONS_ROUTE });
  const tanNavigate = useNavigate({ from: LOCATIONS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<LocationsSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: LocationSortColumn, direction: LocationSortDirection) =>
        navigate({ sort, direction, page: 1 }),
    }),
    [search, navigate],
  );
}
